import { useListPortalTranslationsQuery } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/TranslationsRpc.hooks';
import type { TranslationKey } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/TranslationsRpc';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { useIsUngated } from './useIsUngated';
import { PREFERRED_LANGUAGE_GATE } from '../constants/gates';

export type { TranslationKey };

export const DEFAULT_SUB_KEY = '_default';

export type UsePortalTranslationsResult = {
  portalText: (key: TranslationKey, subKey?: string) => string | undefined;
} & ReturnType<typeof useListPortalTranslationsQuery>;

export function usePortalTranslations(
  locale?: string,
  options?: { skip?: boolean }
): UsePortalTranslationsResult {
  const isPreferredLanguageEnabled = useIsUngated(PREFERRED_LANGUAGE_GATE);

  const { data, ...rest } = useListPortalTranslationsQuery({
    variables: {
      accountId: getPortalAccountId(),
      portalInstanceId: getPortalInstanceId(),
      tolerateInstanceAccessDenied: true,
      ...(locale && { locale }),
    },
    skip: options?.skip || !isPreferredLanguageEnabled,
  });

  const portalText = (
    key: TranslationKey,
    subKey?: string
  ): string | undefined => {
    if (!isPreferredLanguageEnabled) {
      return undefined;
    }

    const resolvedSubKey = subKey ?? DEFAULT_SUB_KEY;
    return data?.translations?.find(
      (entry) =>
        entry.key === key &&
        (entry.subKey ?? DEFAULT_SUB_KEY) === resolvedSubKey
    )?.value;
  };

  return { portalText, data, ...rest };
}
